import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import { baasProxy } from "../../../../../Services/baasConsoleClient";
import { formatBaasEntitySaveError } from "../../../../../Services/baasEntityValidationMessage";
import { fetchBaasInstanceDetail } from "../../../../../Services/baasInstancesApi";
import { formatInputValue } from "../../../../../Utils/formatInputValue";
import { formatBrlMajorMask, parseBrlMajorMask } from "../../Instances/baasInstanceCreateConstants";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const formatPctMask = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  return formatInputValue(String(Math.max(0, Math.round(n * 100))));
};

const parsePctMask = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  if (raw.includes(",")) return parseBrlMajorMask(raw) || 0;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * True when the payload explicitly carries the instance master switch (snake or camel).
 * Missing → undefined (do not treat as off when Administração already shows ON).
 */
const readInstanceAllowOverBalance = (overdraft) => {
  if (overdraft == null || typeof overdraft !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(overdraft, "instance_allow_over_balance")) {
    return Boolean(overdraft.instance_allow_over_balance);
  }
  if (Object.prototype.hasOwnProperty.call(overdraft, "instanceAllowOverBalance")) {
    return Boolean(overdraft.instanceAllowOverBalance);
  }
  return undefined;
};

/**
 * Account-level Over Balance (AllowOverdraft + Limit). Applies to every customer
 * BRL debit — PIX, TED, internal transfer, stables buy — not PIX-only.
 * Hidden/disabled when the instance master switch is off.
 */
export const BaasAccountOverBalancePanel = ({
  accountId,
  disabled = false,
  nested = false,
  inModal = false,
}) => {
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const [allowOverdraft, setAllowOverdraft] = useState(false);
  const [limitDraft, setLimitDraft] = useState("");
  const [instanceAllows, setInstanceAllows] = useState(false);
  const [inheritFees, setInheritFees] = useState(true);
  const [payer, setPayer] = useState("customer");
  const [grantFeePercent, setGrantFeePercent] = useState("");
  const [grantFeeFixed, setGrantFeeFixed] = useState("");
  const [interestPercentPerDay, setInterestPercentPerDay] = useState("");
  const [daysFree, setDaysFree] = useState(0);
  const [catalogName, setCatalogName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = useCallback(async () => {
    if (!selectedInstance || !accountId) return;
    setLoading(true);
    try {
      const [overdraft, instanceDetail] = await Promise.all([
        baasProxy({
          instance: selectedInstance,
          method: "GET",
          baasPath: `v1/accounts/${encodeURIComponent(accountId)}/overdraft`,
        }),
        fetchBaasInstanceDetail(selectedInstance).catch(() => null),
      ]);

      const fromOverdraft = readInstanceAllowOverBalance(overdraft);
      // Prefer explicit overdraft field; if omitted, fall back to instance GET (same card).
      const instanceOn =
        fromOverdraft !== undefined
          ? fromOverdraft
          : Boolean(instanceDetail?.allowOverBalance);

      setInstanceAllows(instanceOn);
      setAllowOverdraft(
        instanceOn && Boolean(overdraft?.allow_overdraft ?? overdraft?.allowOverdraft),
      );
      const limit = Number(overdraft?.limit ?? 0);
      setLimitDraft(
        instanceOn && Number.isFinite(limit) && limit >= 0
          ? formatBrlMajorMask(limit)
          : "",
      );
      setInheritFees(overdraft?.inherit_fees !== false && overdraft?.inheritFees !== false);
      setPayer(
        String(overdraft?.payer ?? "customer").toLowerCase() === "instance"
          ? "instance"
          : "customer",
      );
      setGrantFeePercent(
        formatPctMask(overdraft?.grant_fee_percent ?? overdraft?.grantFeePercent ?? 0),
      );
      setGrantFeeFixed(
        formatBrlMajorMask(overdraft?.grant_fee_fixed ?? overdraft?.grantFeeFixed ?? 0),
      );
      setInterestPercentPerDay(
        formatPctMask(
          overdraft?.interest_percent_per_day ?? overdraft?.interestPercentPerDay ?? 0,
        ),
      );
      setDaysFree(Math.max(0, Math.trunc(Number(overdraft?.days_free ?? overdraft?.daysFree ?? 0) || 0)));
      setCatalogName(
        overdraft?.catalog_instance_name
          || overdraft?.catalogInstanceName
          || instanceDetail?.name
          || "",
      );
    } catch {
      setAllowOverdraft(false);
      setInstanceAllows(false);
      setLimitDraft("");
      setInheritFees(true);
      setPayer("customer");
      setGrantFeePercent("");
      setGrantFeeFixed("");
      setInterestPercentPerDay("");
      setDaysFree(0);
      setCatalogName("");
    } finally {
      setLoading(false);
    }
  }, [selectedInstance, accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!selectedInstance || !accountId || saving || !instanceAllows) return;
    setSaving(true);
    setSaveError("");
    try {
      await baasProxy({
        instance: selectedInstance,
        method: "PATCH",
        baasPath: `v1/accounts/${encodeURIComponent(accountId)}/overdraft`,
        body: {
          allow_overdraft: allowOverdraft,
          limit: allowOverdraft ? parseBrlMajorMask(limitDraft) || 0 : 0,
          inherit_fees: inheritFees,
          payer,
          grant_fee_percent: inheritFees ? undefined : parsePctMask(grantFeePercent),
          grant_fee_fixed: inheritFees ? undefined : parseBrlMajorMask(grantFeeFixed) || 0,
          interest_percent_per_day: inheritFees ? undefined : parsePctMask(interestPercentPerDay),
        },
      });
      addToast({
        type: "success",
        title: intl.get("baas.accounts.detail.limites.overdraftSaved"),
      });
      await load();
    } catch (error) {
      const code = error?.code || error?.error || error?.data?.error;
      const fallback = intl.get("baas.accounts.detail.limites.overdraftLastroError");
      const message =
        code === "overdraft_instance_disabled"
          ? intl.get("baas.accounts.detail.limites.overdraftInstanceDisabled")
          : formatBaasEntitySaveError(error, fallback) || fallback;
      setSaveError(message);
      addToast({
        type: "error",
        title: message,
      });
    } finally {
      setSaving(false);
    }
  };

  const wrapClass = inModal
    ? "space-y-3"
    : nested
      ? "space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40"
      : `${BORDER_CARD} space-y-3 p-4 sm:p-5`;
  const locked = disabled || loading || saving || !instanceAllows;

  return (
    <section className={wrapClass}>
      {inModal ? null : (
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.accounts.detail.limites.overdraftTitle")}
        </p>
      )}
      {!instanceAllows && !loading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftInstanceDisabledHint")}
        </p>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftHint")}
        </p>
      )}
      <label className="flex items-center gap-2 text-sm text-brand-dark dark:text-gray-100">
        <input
          type="checkbox"
          checked={allowOverdraft}
          onChange={(event) => setAllowOverdraft(event.target.checked)}
          disabled={locked}
        />
        {intl.get("baas.accounts.detail.limites.overdraftEnable")}
      </label>
      <div className="max-w-xs">
        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftLimit")}
        </label>
        <input
          className={inputClass}
          value={limitDraft}
          onChange={(event) =>
            setLimitDraft(formatInputValue(String(event.target.value ?? "")))
          }
          placeholder="0,00"
          inputMode="decimal"
          disabled={!allowOverdraft || locked}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-brand-dark dark:text-gray-100">
        <input
          type="checkbox"
          checked={inheritFees}
          onChange={(event) => setInheritFees(event.target.checked)}
          disabled={locked}
        />
        {intl.get("baas.accounts.detail.limites.overdraftInherit", {
          instanceName: catalogName || intl.get("baas.instances.createPage.instanceFallbackName"),
        })}
      </label>
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftPayerLabel")}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-brand-dark dark:text-gray-100">
            <input
              type="radio"
              name="account-overdraft-fee-payer"
              checked={payer === "customer"}
              onChange={() => setPayer("customer")}
              disabled={locked}
            />
            {intl.get("baas.accounts.detail.limites.pixFeePayerCustomer")}
          </label>
          <label className="flex items-center gap-2 text-sm text-brand-dark dark:text-gray-100">
            <input
              type="radio"
              name="account-overdraft-fee-payer"
              checked={payer === "instance"}
              onChange={() => setPayer("instance")}
              disabled={locked}
            />
            {intl.get("baas.accounts.detail.limites.pixFeePayerInstance")}
          </label>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftPayerHint")}
        </p>
      </div>
      {!inheritFees ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.overdraftGrantFeePercent")}
            </label>
            <input
              className={inputClass}
              value={grantFeePercent}
              onChange={(event) =>
                setGrantFeePercent(formatInputValue(String(event.target.value ?? "")))
              }
              inputMode="decimal"
              disabled={locked}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.overdraftGrantFeeFixed")}
            </label>
            <input
              className={inputClass}
              value={grantFeeFixed}
              onChange={(event) =>
                setGrantFeeFixed(formatInputValue(String(event.target.value ?? "")))
              }
              inputMode="decimal"
              disabled={locked}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.overdraftInterestPercent")}
            </label>
            <input
              className={inputClass}
              value={interestPercentPerDay}
              onChange={(event) =>
                setInterestPercentPerDay(formatInputValue(String(event.target.value ?? "")))
              }
              inputMode="decimal"
              disabled={locked}
            />
          </div>
          <p className="self-end text-[11px] text-gray-500 dark:text-gray-400 sm:col-span-2">
            {intl.get("baas.accounts.detail.limites.overdraftDaysFreeHint", { days: daysFree })}
          </p>
        </div>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.overdraftDaysFreeHint", { days: daysFree })}
        </p>
      )}
      {saveError ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {saveError}
        </p>
      ) : null}
      <button
        type="button"
        className="btn-secondary !h-9 !text-xs"
        onClick={handleSave}
        disabled={locked}
      >
        {saving
          ? intl.get("baas.settings.saving")
          : intl.get("baas.accounts.detail.limites.overdraftSave")}
      </button>
    </section>
  );
};
